import { useCallback, useMemo, FC, useRef, useLayoutEffect } from "react";

import { css, cx } from "@/domains/emotion";
import { MdsDropdownButtonItem } from "@/design-system/components/dropdown";
import { mdsSpacings } from "@/design-system/foundations/typography";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { motion } from "motion/react";
import { Theme, useTheme } from "@emotion/react";

export interface MdsDropdownButtonItemComponentProps {
  className?: string;
  item: MdsDropdownButtonItem;
  onOpenChange?: (value: boolean) => void;
  tooltipId: string;
  onHover?: ({ itemId }: { itemId?: string }) => void;
  isSelected?: boolean;
}

export const generateMdsDropdownButtonItemBaseStyles = (theme: Theme) =>
  css({
    display: "flex",
    fontSize: theme.fontSizes.small,
    lineHeight: theme.lineHeights.small,
    flexDirection: "row",
    alignItems: "center",
    height: "auto",
    fontWeight: theme.fontWeights.regular,
    cursor: "pointer",
    transition: "0.08s ease",
    paddingBlock: theme.spacing["2"],
    paddingInline: theme.spacing["5"],
    width: "100%",
    "&:hover": {
      backgroundColor: theme.colors.background.menu.hover,
    },
    "&:active": {
      backgroundColor: theme.colors.background.menu.selected,
    },
    "&:disabled": {
      cursor: "none",
      pointerEvents: "none",
    },
  });

const motionWrapperStyles = css({
  width: "100%",
  display: "flex",
});

/**
 * @deprecated - Use the newer MDS components instead.
 *
 * - If something doesn't exist yet, please create a new MDS component and
 *   have it reviewed by the team!
 *
 * - If something needs to hook into our `stores`, add it to the /components directory.
 *
 * - If something is a one-off; please colocate it with the component/page that
 *   needs it.
 *
 * - If you have questions, please ask in the #hub-eng-web-team Slack channel.
 */
export const MdsDropdownButtonItemComponent: FC<MdsDropdownButtonItemComponentProps> = ({
  className,
  tooltipId,
  item,
  onOpenChange,
  onHover,
  isSelected,
}) => {
  const { onClick: onItemClick, id: itemId } = item;

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onItemClick({ itemId, event });
      onOpenChange?.(false);
    },
    [onItemClick, itemId, onOpenChange]
  );

  const theme = useTheme();
  const mdsDropdownButtonItemBaseStyles = generateMdsDropdownButtonItemBaseStyles(theme);

  const labelStyles = css({
    marginLeft: item.iconKind ? 8 : undefined,
    fontSize: theme.fontSizes.small,
    lineHeight: theme.lineHeights.small,
    color: item.isDisabled ? theme.colors.text.tertiary : theme.colors.text.primary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    width: "100%",
    textAlign: "left",
    display: "flex",
    flexDirection: "column",
    "& .subtitle": {
      fontSize: theme.fontSizes.xsmall,
      lineHeight: theme.lineHeights.xsmall,
      color: item.isDisabled ? theme.colors.text.tertiary : theme.colors.text.secondary,
    },
  });

  const keyboardShortcutStyles = css({
    border: `1px solid ${theme.colors.border.default}`,
    borderRadius: 4,
    color: theme.colors.text.tertiary,
    fontSize: 10,
    fontWeight: 600,
    lineHeight: "16px",
    padding: "0 4px",
  });

  const limitedLabelStyles = css({
    maxWidth: "calc(100% - 140px)",
  });

  const labelDetailStyles = css({
    marginLeft: 4,
    fontSize: theme.fontSizes.small,
    lineHeight: theme.lineHeights.small,
    height: theme.lineHeights.small,
    color: item.isDisabled ? theme.colors.text.tertiary : theme.colors.text.secondary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    width: 140,
    textAlign: "right",
  });

  const isSelectedStyles = css({ backgroundColor: theme.colors.background.menu.hover });
  const buttonStyles = useMemo(
    () =>
      cx(
        className,
        mdsDropdownButtonItemBaseStyles,
        isSelected && isSelectedStyles,
        item.className
      ),
    [className, item.className, mdsDropdownButtonItemBaseStyles, isSelected, isSelectedStyles]
  );

  const iconStyles = css({
    color: item.isDisabled ? theme.colors.icon.placeholder : theme.colors.icon.primary,
    fontSize: 11,
    height: 11,
    width: 11,
  });

  const { iconKind, iconSize } = item;

  const spacerStyles = css({
    flex: 1,
    minWidth: item.isChecked ? mdsSpacings().lg : 0,
  });

  const checkmarkStyles = css({
    alignSelf: item.subtitle && item.content ? "self-start" : "center",
    ["& .mds-icon"]: {
      color: theme.colors.icon.selected,
    },
  });

  const showSelectedLabelDetail = useMemo(() => {
    if (!item.isSelected) {
      return false;
    }

    return Boolean(item.selectedLabelDetail);
  }, [item.isSelected, item.selectedLabelDetail]);

  const icon =
    typeof iconKind === "string" ? (
      <MdsIcon kind={iconKind} innerStyles={{ Icon: { className: iconStyles } }} />
    ) : typeof iconKind === "function" ? (
      iconKind()
    ) : null;
  const wrappedIcon = iconSize ? (
    <div className={css({ height: iconSize, width: iconSize })}>{icon}</div>
  ) : (
    icon
  );

  const ref = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    if (item.scrollIntoView) ref.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [item.scrollIntoView]);

  return (
    <motion.div
      className={motionWrapperStyles}
      key={item.id}
      layoutId={item.animate ? item.id : undefined}
      initial={false}
    >
      <button
        ref={ref}
        className={buttonStyles}
        onClick={handleClick}
        disabled={item.isDisabled}
        data-tip={item.tooltip ?? ""}
        data-for={tooltipId}
        onMouseEnter={() => onHover?.({ itemId })}
        onMouseLeave={() => onHover?.({})}
      >
        {wrappedIcon}
        <div className={cx(labelStyles, showSelectedLabelDetail && limitedLabelStyles)}>
          {item.content ? item.content() : item.label}
          {!item.content && item.subtitle && <div className="subtitle">{item.subtitle}</div>}
        </div>
        {showSelectedLabelDetail && (
          <div className={labelDetailStyles}>{` ${item.selectedLabelDetail ?? ""} `}</div>
        )}
        {(item.isChecked || item.keyboardShortcut) && <div className={spacerStyles} />}
        {item.isChecked && <MdsIcon kind={MdsIconKind.Check} className={checkmarkStyles} />}
        {item.keyboardShortcut && !item.isChecked && (
          <span className={keyboardShortcutStyles}>{item.keyboardShortcut}</span>
        )}
      </button>
    </motion.div>
  );
};
